import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, timeAgo } from '@/lib/api';
import { MessageCircle, Search } from 'lucide-react';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadThreads();
    const interval = setInterval(loadThreads, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadThreads = async () => {
    try {
      const res = await api.get('/messages/threads');
      setThreads(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const getOtherParticipant = (thread) => {
    const otherId = thread.participants.find(p => p !== user?.user_id);
    return {
      id: otherId,
      name: thread.participant_names?.[otherId] || 'Unknown',
      picture: thread.participant_pictures?.[otherId] || '',
    };
  };

  const getUnread = (thread) => {
    return thread.unread_count?.[user?.user_id] || 0;
  };

  const filtered = threads.filter(t => {
    if (!search) return true;
    const other = getOtherParticipant(t);
    return other.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto" data-testid="messages-page">
      <SEO title="Messages" description="Chat with organizations and teens on TeenHands. Coordinate volunteer shifts, side gigs, and more." path="/messages" />
      <h1 className="text-3xl font-bold text-[#011627] mb-4 animate-fade-in-up">Messages</h1>

      <div className="relative mb-4 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA5B1]" />
        <Input
          placeholder="Search conversations..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="pl-11 bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
          data-testid="messages-search"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-4 border-2 border-gray-100 animate-pulse flex gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100" />
              <div className="flex-1">
                <div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-8 border-2 border-gray-100 text-center animate-fade-in">
          <MessageCircle size={40} className="text-[#9AA5B1] mx-auto mb-3" />
          <p className="text-[#5C6B7F] font-semibold">No conversations yet</p>
          <p className="text-sm text-[#9AA5B1]">Start chatting by messaging someone from a gig!</p>
        </div>
      ) : (
        <div className="space-y-2 stagger-children">
          {filtered.map(thread => {
            const other = getOtherParticipant(thread);
            const unread = getUnread(thread);
            return (
              <button
                key={thread.thread_id}
                onClick={() => navigate(`/messages/${thread.thread_id}`)}
                className={`w-full bg-white rounded-2xl p-4 border-2 flex items-center gap-3 text-left card-hover animate-fade-in-up ${
                  unread > 0 ? 'border-[#2EC4B6]/30' : 'border-gray-100'
                }`}
                data-testid={`thread-${thread.thread_id}`}
              >
                {other.picture ? (
                  <img src={other.picture} alt={other.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {other.name?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[#011627]">{other.name}</span>
                    <span className="text-[10px] text-[#9AA5B1]">{timeAgo(thread.last_message_at)}</span>
                  </div>
                  <p className="text-sm text-[#5C6B7F] truncate">{thread.last_message}</p>
                </div>
                {unread > 0 && (
                  <span className="w-6 h-6 bg-[#2EC4B6] rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
